import { useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { amplitudeTrack, getAnalyticsUser } from '../amplitude/amplitude';
import * as Sentry from '@sentry/react';
import { ACCESS_TOKEN_JWT_KEY } from '../../utils/auth';
import { BACKEND_USER_ANALYTICS_URL } from 'config/constants';
import { useFeatureFlag } from '../../components/utils/use-feature-flag';
import { isEmpty, difference } from 'lodash';

const REQUIRED_FIELDS_FOR_CLASS_TRACKING = ['_id', 'classCode', 'learningSubject', 'year'];

export const trackUserAnalytics = (trackingPayload) => {
  const {
    eventType,
    trackData = {},
    eventProperties = {},
    avoidBackendTracking = false,
  } = trackingPayload;

  try {
    if (!eventType) throw new Error('Event name must be defined');
    amplitudeTrack(eventType, eventProperties);
    const { amplitudeSessionId, deviceId } = getAnalyticsUser();
    if (avoidBackendTracking) return;
    serverTrack({
      amplitudeSessionId,
      eventType,
      ...trackData,
      eventData: { deviceId, ...(eventProperties || {}) },
    });
  } catch (err) {
    Sentry.captureException(err);
  }
};

const serverTrack = (eventProperties) => {
  try {
    const AUTHORIZATION_TOKEN = localStorage.getItem(ACCESS_TOKEN_JWT_KEY);
    const requestOptions = {
      method: 'POST',
      headers: {
        authorization: AUTHORIZATION_TOKEN || '',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventProperties),
    };

    fetch(BACKEND_USER_ANALYTICS_URL, requestOptions);
  } catch (err) {
    Sentry.captureException(err);
  }
};

export const buildSchoolClassTrackPayload = (schoolClass = {}) => {
  const missingClassFields = difference(
    REQUIRED_FIELDS_FOR_CLASS_TRACKING,
    Object.keys(schoolClass)
  );

  if (!isEmpty(missingClassFields)) {
    Sentry.captureMessage(
      `Missing the following fields in schoolClass for tracking ${missingClassFields.join(', ')}`
    );
  }

  return {
    schoolClassCode: schoolClass.classCode,
    learningSubject: schoolClass.learningSubject,
    schoolClassId: schoolClass._id,
    grade: schoolClass.year,
  };
};

/*
Note that the `track` function signature and this hook generally are intentionally
designed *not* to have knowledge of either MeeOpp or vendor APIs and domain concepts.

For this reason, `track` accepts a simple, schema-less payload and can be considered
a low-level API. To apply standardized formatting and validation to your payloads,
please consider building such logic outside of this hook.

For more context or if you're unsure, consult with other engineers for guidance.
*/
export const useBaseTrackUserAnalytics = (
  trackingOptions = {
    avoidBackendTracking: false,
    featureFlag: null,
    featureFlagResult: { isEnabled: true, isLoading: false },
    user: null,
  }
) => {
  const trackingQueueRef = useRef([]);

  const {
    avoidBackendTracking,
    featureFlag,
    featureFlagResult = { isEnabled: true, isLoading: false },
    user,
  } = trackingOptions;

  const { isEnabled: isTrackAnalyticsEnabled, isLoading } = featureFlagResult;

  const track = useCallback(
    (eventType, payload) => {
      if (isLoading && !!featureFlag) {
        trackingQueueRef.current.push([eventType, payload]);
        return;
      }

      const trackPayload = {
        schoolClassId: payload?.schoolClassId || user?.schoolClassId || null,
      };

      if (isTrackAnalyticsEnabled || !featureFlag) {
        trackUserAnalytics({
          eventType,
          trackPayload,
          eventProperties: payload?.eventProperties,
          avoidBackendTracking,
        });
      }
    },
    [user, isTrackAnalyticsEnabled, featureFlag, avoidBackendTracking, isLoading]
  );

  useEffect(() => {
    if (isLoading || !isTrackAnalyticsEnabled) {
      return;
    }

    trackingQueueRef.current.forEach(([eventType, payload]) => {
      track(eventType, payload);
    });

    trackingQueueRef.current = [];
  }, [isLoading, isTrackAnalyticsEnabled, track]);

  return [track];
};

export const useTrackUserAnalytics = (
  trackingOptions = { avoidBackendTracking: false, featureFlag: null }
) => {
  const user = useSelector((store) => store?.auth?.user);
  const featureFlagResult = useFeatureFlag(trackingOptions.featureFlag);
  return useBaseTrackUserAnalytics({ ...trackingOptions, featureFlagResult, user });
};
